import { useState, useEffect, useCallback } from 'preact/hooks';
import { getIngredient, updateIngredient } from '../../api/ingredients';
import { Button } from '../../components/button';
import { Card } from '../../components/card';
import { Input } from '../../components/input';
import { Textarea } from '../../components/textarea';
import { Select } from '../../components/select';
import { ToggleChip } from '../../components/toggle-chip';
import { Spinner } from '../../components/spinner';
import { Message } from '../../components/message';
import type { Ingredient } from '../../api/types';

interface IngredientDetailProps {
  id: number;
  onBack: () => void;
}

function toBool(v: number | null): boolean {
  return v === 1;
}
function toDbInt(v: boolean): number {
  return v ? 1 : 0;
}

const HALAL_OPTIONS = [
  { value: '', label: '\u2014 none \u2014' },
  { value: 'halal', label: 'Halal' },
  { value: 'haram', label: 'Haram' },
  { value: 'mushbooh', label: 'Mushbooh' },
];

const KOSHER_OPTIONS = [
  { value: '', label: '\u2014 none \u2014' },
  { value: 'kosher', label: 'Kosher' },
  { value: 'not_kosher', label: 'Not Kosher' },
  { value: 'pareve', label: 'Pareve' },
];

const GI_OPTIONS = [
  { value: '', label: '\u2014 none \u2014' },
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
];

const FODMAP_OPTIONS = [
  { value: '', label: '\u2014 none \u2014' },
  { value: 'fructose', label: 'Fructose' },
  { value: 'lactose', label: 'Lactose' },
  { value: 'fructans', label: 'Fructans' },
  { value: 'galactans', label: 'Galactans' },
  { value: 'polyols', label: 'Polyols' },
];

const NOVA_OPTIONS = [
  { value: '', label: '\u2014 none \u2014' },
  { value: '1', label: '1 \u2014 Unprocessed' },
  { value: '2', label: '2 \u2014 Processed Ingredients' },
  { value: '3', label: '3 \u2014 Processed Foods' },
  { value: '4', label: '4 \u2014 Ultra-processed' },
];

const ADDITIVE_FUNCTION_OPTIONS = [
  { value: '', label: '\u2014 none \u2014' },
  { value: 'antioxidant', label: 'Antioxidant' },
  { value: 'colour', label: 'Colour' },
  { value: 'emulsifier', label: 'Emulsifier' },
  { value: 'flavour_enhancer', label: 'Flavour Enhancer' },
  { value: 'gelling_agent', label: 'Gelling Agent' },
  { value: 'humectant', label: 'Humectant' },
  { value: 'preservative', label: 'Preservative' },
  { value: 'stabiliser', label: 'Stabiliser' },
  { value: 'sweetener', label: 'Sweetener' },
  { value: 'thickener', label: 'Thickener' },
  { value: 'other', label: 'Other' },
];

const REGULATORY_OPTIONS = [
  { value: '', label: '\u2014 none \u2014' },
  { value: 'approved', label: 'Approved' },
  { value: 'restricted', label: 'Restricted' },
  { value: 'banned', label: 'Banned' },
  { value: 'under_review', label: 'Under Review' },
];

const VERIFICATION_OPTIONS = [
  { value: '', label: '\u2014 none \u2014' },
  { value: 'manual', label: 'Manual' },
  { value: 'openfoodfacts', label: 'Open Food Facts' },
  { value: 'wikidata', label: 'Wikidata' },
  { value: 'ai', label: 'AI' },
];

type FormState = Record<string, unknown>;

function initForm(i: Ingredient): FormState {
  return { ...i };
}

interface CollapsibleSectionProps {
  title: string;
  defaultOpen?: boolean;
  children: preact.ComponentChildren;
}

function CollapsibleSection({ title, defaultOpen = true, children }: CollapsibleSectionProps) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <Card>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          userSelect: 'none',
        }}
        onClick={() => setOpen(!open)}
      >
        <h2 style={{ marginBottom: 0 }}>{title}</h2>
        <span style={{ color: 'var(--text-tertiary)', fontSize: '0.76rem', transition: 'transform 0.2s' }}>
          {open ? '\u25B2' : '\u25BC'}
        </span>
      </div>
      {open && <div style={{ marginTop: 'var(--sp-3)' }}>{children}</div>}
    </Card>
  );
}

export function IngredientDetail({ id, onBack }: IngredientDetailProps) {
  const [ingredient, setIngredient] = useState<Ingredient | null>(null);
  const [form, setForm] = useState<FormState>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    getIngredient(id)
      .then((res) => {
        setIngredient(res.ingredient);
        setForm(initForm(res.ingredient));
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load'))
      .finally(() => setLoading(false));
  }, [id]);

  const setField = useCallback((key: string, value: unknown) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleSave = async () => {
    if (!ingredient) return;
    setSaving(true);
    setError(null);
    setSuccessMsg(null);

    const dirty: Record<string, unknown> = {};
    const original = initForm(ingredient);
    for (const key of Object.keys(form)) {
      if (key === 'id' || key === 'created_at' || key === 'updated_at') continue;
      if (form[key] !== original[key]) {
        dirty[key] = form[key];
      }
    }

    if (Object.keys(dirty).length === 0) {
      setSuccessMsg('No changes to save');
      setSaving(false);
      return;
    }

    try {
      const res = await updateIngredient(id, dirty);
      setIngredient(res.ingredient);
      setForm(initForm(res.ingredient));
      setSuccessMsg(`Saved. ${res.affectedProducts} product(s) affected.`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // Render helpers
  function textField(label: string, key: string, opts?: { placeholder?: string }) {
    return (
      <div class="form-row">
        <label>{label}</label>
        <Input
          value={String(form[key] ?? '')}
          onChange={(v) => setField(key, v || null)}
          placeholder={opts?.placeholder}
        />
      </div>
    );
  }

  function textareaField(label: string, key: string, opts?: { rows?: number; placeholder?: string }) {
    return (
      <div class="form-row" style={{ gridColumn: '1 / -1' }}>
        <label>{label}</label>
        <Textarea
          value={String(form[key] ?? '')}
          onChange={(v) => setField(key, v || null)}
          rows={opts?.rows ?? 3}
          placeholder={opts?.placeholder}
        />
      </div>
    );
  }

  function selectField(
    label: string,
    key: string,
    options: { value: string; label: string }[],
    opts?: { numeric?: boolean },
  ) {
    return (
      <div class="form-row">
        <label>{label}</label>
        <Select
          value={String(form[key] ?? '')}
          onChange={(v) => {
            if (!v) setField(key, null);
            else if (opts?.numeric) setField(key, Number(v));
            else setField(key, v);
          }}
          options={options}
        />
      </div>
    );
  }

  function chipField(label: string, key: string) {
    return (
      <ToggleChip
        label={label}
        active={toBool(form[key] as number | null)}
        onChange={(v) => setField(key, toDbInt(v))}
      />
    );
  }

  function numberField(label: string, key: string) {
    return (
      <div class="form-row">
        <label>{label}</label>
        <Input
          value={form[key] != null ? String(form[key]) : ''}
          onChange={(v) => setField(key, v ? Number(v) : null)}
          placeholder="0.0"
        />
      </div>
    );
  }

  function dateField(label: string, key: string) {
    return (
      <div class="form-row">
        <label>{label}</label>
        <Input
          value={String(form[key] ?? '').slice(0, 10)}
          onChange={(v) => setField(key, v || null)}
          type="date"
        />
      </div>
    );
  }

  if (loading) return <Spinner text="Loading ingredient" centered />;
  if (error && !ingredient) return <Message text={error} type="error" />;
  if (!ingredient) return null;

  return (
    <div>
      {/* Header */}
      <div class="toolbar">
        <Button variant="ghost" size="sm" onClick={onBack}>
          &larr; Back to list
        </Button>
        <h3 style={{ margin: 0, flex: 1 }}>{ingredient.canonical_name}</h3>
        <Button onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      {error && <Message text={error} type="error" />}
      {successMsg && <Message text={successMsg} type="success" onDismiss={() => setSuccessMsg(null)} />}

      {/* Identity */}
      <CollapsibleSection title="Identity">
        <div class="form-grid">
          {textField('Canonical Name', 'canonical_name')}
          {textField('E-Number', 'e_number', { placeholder: 'E100' })}
          {textField('INS Number', 'ins_number', { placeholder: '100' })}
          {textField('OFF ID', 'off_id', { placeholder: 'en:ingredient-name' })}
          {textField('Wikidata ID', 'wikidata_id', { placeholder: 'Q12345' })}
          {textareaField('Description', 'description', { rows: 3 })}
          {textareaField('Aliases (JSON array)', 'aliases', { rows: 2, placeholder: '["alias1", "alias2"]' })}
        </div>
      </CollapsibleSection>

      {/* Source Classification */}
      <CollapsibleSection title="Source Classification">
        <div class="toggle-chips">
          {chipField('Animal Derived', 'is_animal_derived')}
          {chipField('Plant Derived', 'is_plant_derived')}
          {chipField('Synthetic', 'is_synthetic')}
          {chipField('Insect Derived', 'is_insect_derived')}
          {chipField('Microbial Derived', 'is_microbial_derived')}
          {chipField('Mushroom Derived', 'is_mushroom_derived')}
          {chipField('Mineral Derived', 'is_mineral_derived')}
          {chipField('Fermented', 'is_fermented')}
          {chipField('Source Ambiguous', 'source_ambiguous')}
          {chipField('Requires Slaughter', 'requires_slaughter')}
        </div>
        <div class="form-grid" style={{ marginTop: 'var(--sp-3)' }}>
          {textField('Possible Sources', 'possible_sources')}
        </div>
      </CollapsibleSection>

      {/* Religious Compliance */}
      <CollapsibleSection title="Religious Compliance">
        <div class="form-grid">
          {selectField('Halal Status Override', 'halal_status_override', HALAL_OPTIONS)}
          {selectField('Kosher Status', 'kosher_status', KOSHER_OPTIONS)}
        </div>
        <div class="toggle-chips" style={{ marginTop: 'var(--sp-3)' }}>
          {chipField('Alcohol Ingredient', 'is_alcohol_ingredient')}
        </div>
      </CollapsibleSection>

      {/* Allergens */}
      <CollapsibleSection title="Allergens (EU 14+)">
        <div class="toggle-chips">
          {chipField('Gluten', 'contains_gluten')}
          {chipField('Dairy', 'contains_dairy')}
          {chipField('Nuts', 'contains_nuts')}
          {chipField('Peanuts', 'contains_peanuts')}
          {chipField('Soy', 'contains_soy')}
          {chipField('Fish', 'contains_fish')}
          {chipField('Shellfish', 'contains_shellfish')}
          {chipField('Sesame', 'contains_sesame')}
          {chipField('Egg', 'contains_egg')}
          {chipField('Alcohol', 'contains_alcohol')}
          {chipField('Mustard', 'contains_mustard')}
          {chipField('Celery', 'contains_celery')}
          {chipField('Lupin', 'contains_lupin')}
          {chipField('Mollusks', 'contains_mollusks')}
          {chipField('Sulfites', 'contains_sulfites')}
          {chipField('Wheat', 'contains_wheat')}
          {chipField('Corn', 'contains_corn')}
        </div>
      </CollapsibleSection>

      {/* Glycemic / Diabetic */}
      <CollapsibleSection title="Glycemic / Diabetic">
        <div class="form-grid">
          {selectField('Glycemic Index Category', 'glycemic_index_category', GI_OPTIONS)}
        </div>
        <div class="toggle-chips" style={{ marginTop: 'var(--sp-3)' }}>
          {chipField('High Sugar', 'is_high_sugar')}
          {chipField('High Carb', 'is_high_carb')}
          {chipField('Sugar Alcohol', 'is_sugar_alcohol')}
          {chipField('Artificial Sweetener', 'is_artificial_sweetener')}
          {chipField('Natural Sweetener', 'is_natural_sweetener')}
        </div>
      </CollapsibleSection>

      {/* FODMAP / Histamine */}
      <CollapsibleSection title="FODMAP / Histamine">
        <div class="form-grid">
          {selectField('FODMAP Category', 'fodmap_category', FODMAP_OPTIONS)}
        </div>
        <div class="toggle-chips" style={{ marginTop: 'var(--sp-3)' }}>
          {chipField('High FODMAP', 'is_high_fodmap')}
          {chipField('High Histamine', 'is_high_histamine')}
          {chipField('Histamine Liberator', 'is_histamine_liberator')}
          {chipField('DAO Inhibitor', 'is_dao_inhibitor')}
        </div>
      </CollapsibleSection>

      {/* Elimination Diet */}
      <CollapsibleSection title="Elimination Diet">
        <div class="toggle-chips">
          {chipField('Nightshade', 'is_nightshade')}
          {chipField('Legume', 'is_legume')}
          {chipField('Grain', 'is_grain')}
          {chipField('Seed Oil', 'is_seed_oil')}
        </div>
      </CollapsibleSection>

      {/* Nutritional / Health */}
      <CollapsibleSection title="Nutritional / Health">
        <div class="toggle-chips">
          {chipField('High Sodium', 'is_high_sodium')}
          {chipField('High Potassium', 'is_high_potassium')}
          {chipField('High Phosphorus', 'is_high_phosphorus')}
          {chipField('Phosphate Additive', 'is_phosphate_additive')}
          {chipField('High Saturated Fat', 'is_high_saturated_fat')}
          {chipField('Trans Fat', 'is_trans_fat')}
          {chipField('High Oxalate', 'is_high_oxalate')}
          {chipField('High Purine', 'is_high_purine')}
          {chipField('High Fiber', 'is_high_fiber')}
          {chipField('Omega-3 Source', 'is_omega_3_source')}
          {chipField('Cholesterol Source', 'is_cholesterol_source')}
          {chipField('Caffeine Source', 'is_caffeine_source')}
          {chipField('High Calcium', 'is_high_calcium')}
          {chipField('Contains Palm Oil', 'contains_palm_oil')}
        </div>
      </CollapsibleSection>

      {/* Processing & Safety */}
      <CollapsibleSection title="Processing & Safety">
        <div class="form-grid">
          {selectField('NOVA Group', 'nova_group', NOVA_OPTIONS, { numeric: true })}
          {selectField('Additive Function', 'additive_function', ADDITIVE_FUNCTION_OPTIONS)}
          {selectField('Regulatory Status', 'regulatory_status', REGULATORY_OPTIONS)}
        </div>
        <div class="toggle-chips" style={{ marginTop: 'var(--sp-3)' }}>
          {chipField('Artificial Color', 'is_artificial_color')}
          {chipField('Artificial Preservative', 'is_artificial_preservative')}
          {chipField('Artificial Flavor', 'is_artificial_flavor')}
          {chipField('Emulsifier', 'is_emulsifier')}
          {chipField('GMO Risk', 'is_gmo_risk')}
        </div>
      </CollapsibleSection>

      {/* Metadata */}
      <CollapsibleSection title="Metadata">
        <div class="toggle-chips" style={{ marginBottom: 'var(--sp-3)' }}>
          {chipField('Auto Learned', 'auto_learned')}
        </div>
        <div class="form-grid">
          {numberField('Confidence Score', 'confidence_score')}
          {selectField('Verification Source', 'verification_source', VERIFICATION_OPTIONS)}
          {dateField('Last Verified At', 'last_verified_at')}
        </div>
      </CollapsibleSection>
    </div>
  );
}
